import React, { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import {
  FiArrowLeft,
  FiFileText,
  FiMapPin,
  FiUser,
  FiAward,
  FiDollarSign,
} from "react-icons/fi";
import PropertyComment from "../property/PropertyComment";
import { showToast } from "../../../components/common/notification/Notification";
import styles from "./PromotionDiscussionPage.module.css";

const PromotionDiscussionPage = ({ back = "/customer/promotions" }) => {
  const { Id } = useParams();
  const navigate = useNavigate();

  const [promoData, setPromoData] = useState(null);
  // eslint-disable-next-line no-unused-vars
  const [loadingPromo, setLoadingPromo] = useState(false);

  const loggedInUser = useSelector((state) => state.auth?.user);
  const token =
    loggedInUser?.accessToken ||
    loggedInUser?.token ||
    loggedInUser?.refreshToken;
  const baseUrl = process.env.REACT_APP_BASE_URL;

  useEffect(() => {
    if (Id) {
      fetchPromotionSummary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [Id]);

  const fetchPromotionSummary = async () => {
    try {
      setLoadingPromo(true);
      const response = await axios.get(`${baseUrl}/promotion/${Id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.status === 200) {
        const promo = response.data.promotion || response.data;
        const owner = promo.user?.id || promo.partner?.id || promo.user_id;

        const rawFirstName = promo.partner?.first_name || promo.user?.first_name;
        const rawLastName = promo.partner?.last_name || promo.user?.last_name;
        let cleanName = [rawFirstName, rawLastName].filter(Boolean).join(" ");
        if (!cleanName || cleanName.toLowerCase().includes("null")) {
          cleanName = promo.user?.name || "Partner";
        }

        setPromoData({
          promotionId: promo.id || Id,
          ownerId: owner,
          title: promo.project_name || promo.title || "Promotion Details",
          creatorName: cleanName,
          type: promo.property_type?.name || promo.type,
          city: promo.city_name,
          state: promo.state_name,
          price: promo.starting_cost || promo.cost,
        });
      }
    } catch (err) {
      console.error("Failed to load promotion summary for discussion:", err);
      showToast("Error loading promotion information", { type: "error" });
    } finally {
      setLoadingPromo(false);
    }
  };

  const normalizedBack = back.replace(/\/+$/, "");
  const detailsPath = `${normalizedBack}/${Id}`;

  const formattedPrice =
    promoData?.price !== undefined &&
    promoData?.price !== null &&
    promoData?.price !== ""
      ? isNaN(Number(promoData.price))
        ? promoData.price
        : `₹ ${Number(promoData.price).toLocaleString("en-IN")}`
      : null;

  return (
    <div className={styles.wrapper}>
      {/* 1. Page Header */}
      <div className={styles.pageHeader}>
        <div className={styles.pageHeaderLeft}>
          <button
            type="button"
            className={styles.headerBackBtn}
            onClick={() => navigate(detailsPath)}
            aria-label="Back to promotion details"
            title="Back to promotion details"
          >
            <FiArrowLeft size={16} />
            <span>Promotion Details</span>
          </button>
          <div className={styles.headerTitles}>
            <span className={styles.pageCategory}>Community Discussion</span>
            <h1 className={styles.pageTitle}>Promotion Discussion & Comments</h1>
          </div>
        </div>
      </div>

      {/* 2. Compact Promotion Summary Banner */}
      <div className={styles.summaryCard}>
        <div className={styles.summaryLeft}>
          <div className={styles.summaryIconBox}>
            <FiAward />
          </div>
          <div>
            <h3 className={styles.summaryTitle}>
              {promoData?.title || `Promotion Project #${Id}`}
            </h3>
            <div className={styles.summaryMeta}>
              {promoData?.creatorName && (
                <span className={styles.metaItem}>
                  <FiUser className="text-muted" />
                  <span>Promoted by {promoData.creatorName}</span>
                </span>
              )}
              {promoData?.type && (
                <span className={styles.metaItem}>
                  <span className="badge bg-light text-primary border border-primary-subtle">
                    {promoData.type}
                  </span>
                </span>
              )}
              {(promoData?.city || promoData?.state) && (
                <span className={styles.metaItem}>
                  <FiMapPin className="text-danger" />
                  <span>
                    {[promoData.city, promoData.state]
                      .filter(Boolean)
                      .join(", ")}
                  </span>
                </span>
              )}
              {formattedPrice && (
                <span className={`${styles.metaItem} ${styles.priceTag}`}>
                  <FiDollarSign className="text-success" />
                  <span>{formattedPrice}</span>
                </span>
              )}
            </div>
          </div>
        </div>

        <div className={styles.summaryRight}>
          <button
            type="button"
            className={styles.summaryDetailsBtn}
            onClick={() => navigate(detailsPath)}
          >
            <FiFileText size={14} />
            <span>View Full Details</span>
          </button>
        </div>
      </div>

      {/* 3. Discussion & Comment Thread */}
      <div className={styles.discussionMain}>
        <PropertyComment
          Id={Id}
          ownerId={promoData?.ownerId}
          commentType="promotionComments"
          allComments="/property/requestPromotionComments"
          addComment="/property/addRequestPromotionComment"
          editComment="/property/editRequestPromotionComment"
          deleteComment="/property/deleteRequestPromotionComment/"
        />
      </div>
    </div>
  );
};

export default PromotionDiscussionPage;
